import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import {
  createGiftOrder,
  createDigioKycRequest,
  createGiftOutboundOrder,
  generateSigningRequest,
  getDigioKycRequestStatus,
  getExchangeRates,
  getGiftOrder,
  getGiftOutboundOrder,
  getGiftSubmissions,
  getLRSFXQuote,
  getPaymentGatewayToken,
  notifyGiftPaymentLink,
  submitGiftKycApi,
  verifySdkResponse,
  getGiftLatestSumissionInfo,
} from "../api/gift";

export type {
  SubmitGiftKycPayload,
  SubmitGiftKycResponse,
} from "../types/gift";

export function useSubmitGiftKyc() {
  return useMutation({
    mutationFn: submitGiftKycApi,
  });
}

export function useGenerateSignRequest(sendEmail: boolean) {
  return useMutation({
    mutationKey: ["signing-request", sendEmail],
    mutationFn: (formId: number) =>
      generateSigningRequest({ formId, sendEmail }),
  });
}

export function useCreateDigioKycRequest() {
  return useMutation({
    mutationFn: createDigioKycRequest,
  });
}

export const digioKycRequestStatusQueryOptions = (
  variables: Parameters<typeof getDigioKycRequestStatus>[0],
  {
    enabled = true,
    refetchInterval = false,
  }: { enabled?: boolean; refetchInterval?: number | false } = {}
) => {
  return queryOptions({
    queryKey: ["digio-kyc-request-status", variables],
    queryFn: () => getDigioKycRequestStatus(variables),
    enabled,
    refetchInterval,
  });
};

export function useDigioKycRequestStatus(
  variables: Parameters<typeof getDigioKycRequestStatus>[0],
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  return useQuery(digioKycRequestStatusQueryOptions(variables, options));
}

export function useGiftSubmissions(investmentAccountId: string) {
  return useQuery({
    queryKey: ["gift-submission", investmentAccountId],
    queryFn: () => getGiftSubmissions(investmentAccountId),
  });
}

export function useLatestGiftInfo(
  investmentAccountId: string,
  options?: { enabled: boolean }
) {
  return useQuery({
    queryKey: ["gift-submission", investmentAccountId, "info"],
    queryFn: () => getGiftLatestSumissionInfo({ investmentAccountId }),
    ...options,
  });
}

export function useLrsFxQuote(
  variables: Parameters<typeof getLRSFXQuote>[0],
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [
      "lrs-fx-quote",
      variables.fundSchemeId,
      variables.bankAccountId,
      variables.amount,
    ],
    queryFn: () => getLRSFXQuote(variables),
    ...options,
  });
}

export function useCreateGiftOrder() {
  return useMutation({
    mutationFn: createGiftOrder,
  });
}

export function useCreateGiftOutboundOrder() {
  return useMutation({
    mutationFn: createGiftOutboundOrder,
  });
}

export function useNotifyGiftPaymentLink() {
  return useMutation({
    mutationFn: notifyGiftPaymentLink,
  });
}

export const getGiftOutboundQueryOption = (
  variables: Parameters<typeof getGiftOutboundOrder>[0]
) =>
  queryOptions({
    queryKey: ["gift", "outbound", variables.orderId],
    queryFn: () => getGiftOutboundOrder(variables),
  });

export function useGiftOutboundOrder(
  variables: Parameters<typeof getGiftOutboundOrder>[0]
) {
  return useQuery(getGiftOutboundQueryOption(variables));
}

export const getGiftOrderQueryOption = (
  variables: Parameters<typeof getGiftOrder>[0]
) =>
  queryOptions({
    queryKey: ["gift", "order", variables.orderId],
    queryFn: () => getGiftOrder(variables),
  });

export function useGiftOrder(variables: Parameters<typeof getGiftOrder>[0]) {
  return useQuery(getGiftOrderQueryOption(variables));
}

export const paymentGatewayTokenQueryOption = (
  variables: Parameters<typeof getPaymentGatewayToken>[0],
  { enabled = true } = {}
) =>
  queryOptions({
    queryKey: ["gift", "payment-gateway-token", variables.fundSchemeId],
    queryFn: () => getPaymentGatewayToken(variables),
    enabled,
  });

export function usePaymentGatewayToken(
  variables: Parameters<typeof getPaymentGatewayToken>[0],
  options?: { enabled?: boolean }
) {
  return useQuery(paymentGatewayTokenQueryOption(variables, options));
}

export function useVerifySDKResponse() {
  return useMutation({
    mutationFn: verifySdkResponse,
  });
}

export function useExchangeRates(
  variables: Parameters<typeof getExchangeRates>[0],
  { enabled = true }: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ["exchange-rates", variables],
    queryFn: () => getExchangeRates(variables),
    enabled,
  });
}
